// src/hooks/useGoogleAnalytics.ts
import { useCallback, useEffect } from 'react'
import { isGAEnabled } from '../lib/analytics'
import { useMe } from '../pages/meeting-view/hooks/useMe'

/**
 * Custom hook that provides functions to send GA4 events and pageviews.
 * Assumes initGoogleAnalytics() was called at app startup.
 */
export function useGoogleAnalytics() {
    const { user } = useMe()

    useEffect(() => {
        if (!isGAEnabled || !window.GA) return
        window.GA.set({
            userId: user?.userId,
            userProperties: {
                organizationId: user?.organizationId,
                organizationName: user?.organizationName,
            },
        })
    }, [user])

    /**
     * Send a custom event to GA4.
     */
    const sendEvent = useCallback((props: GAEventProps) => {
        if (!isGAEnabled || !window.GA) {
            console.warn(`GA not ready — event "${props.action}" (category: "${props.category}") not sent.`)
            return
        }

        const { category, ...restProps } = props

        window.GA.event({
            category,
            ...restProps,
        })
    }, [])

    /**
     * Send a page_view event to GA4.
     * @param path Optional URL path; defaults to window.location.pathname.
     */
    const sendPageView = useCallback((path?: string) => {
        if (!isGAEnabled || !window.GA) {
            console.warn(`GA not ready — page_view for "${path || window.location.pathname}" not sent.`)
            return
        }

        window.GA.send({
            hitType: 'page_view',
            page_path: path || window.location.pathname,
        })
    }, [])

    return { sendEvent, sendPageView }
}

// Interfaces

type GACategories = 'meeting' | 'meeting_error' | 'screen_share'

export interface GAEventProps {
    category: GACategories
    action: string
    label?: string
    value?: number
    nonInteraction?: boolean
    transport?: 'beacon' | 'xhr' | 'image'
    // any other custom parameters
    [key: string]: any
}
